// apps/share-web/src/chart/chart-now-next.ts
//
// Строка «где песня СЕЙЧАС → что ДАЛЬШЕ» в шапке поверхности чтения.
//
// В полном экране она была с волны A. В панели её не было, и причина стояла в
// шапке chart-panel.ts: «показывать её нечем, панель на тик не подписана, а
// пустая строка это просто дыра в шапке». Сергей на приёмке попросил её и туда —
// «да, это нужно», — и причина снялась вместе с кадром, который панель теперь
// гонит сама.
//
// Модуль отдельный, а не по копии на поверхность, ровно поэтому: «дальше» в
// панели и «дальше» в слое обязаны быть одной правдой об одной песне. Копия
// поиска секции уже стоила долга (TD-260828-section-at-duplicated-in-two-web-
// modules) — третья была бы неприличной, и здесь её нет: секцию ищет
// экспортированный sectionIndexAt из chart-follow.ts, то есть ровно то же, чем
// ведение красит активный блок. Иначе шапка называла бы одну секцию, пока лист
// подсвечивает другую.
//
// Пустое значение — НЕ строка: «нечем заполнить» рисуется чертой (DASH), а
// песня, которой нечего называть (ни одной НАЗВАННОЙ секции), ряда не получает
// вовсе — две черты подряд это дыра в шапке, а не сведения. Гейт стоит здесь,
// один на две поверхности, и наружу это выглядит как `null` вместо ручки.
//
// Расписание приезжает ПОЛНЫМ, с безымянными секциями: ряд и ведение листа
// обязаны говорить об одном мгновении одной песни, а ведение следит по всей
// аранжировке (`gridSections` в chart-song.ts). Поэтому «сейчас» умеет быть
// чертой посреди песни (идёт проигрыш), а «дальше» в этот момент называет
// следующую НАЗВАННУЮ секцию — то самое, чего в проигрыше и ждут.

import { sectionIndexAt, type SectionSec } from './chart-follow.js';

/** Нечем заполнить — честная черта, а не пустое место. */
const DASH = '—';

export interface NowNextDeps {
  /**
   * ПОЛНОЕ расписание песни — с безымянными секциями тоже: ряд обязан говорить
   * об одной правде с ведением листа, а следит оно по всей аранжировке
   * (`gridSections` в chart-song.ts). Ни одной названной секции — ряда не будет
   * вовсе.
   */
  sections: SectionSec[];
  /**
   * Класс МЕСТА в шапке: у слоя это `cw-fs-row3` (свой отбив и разделитель
   * снизу), у панели — `cw-panel-nn`. Типографику ряд несёт сам (`cw-nn`), и
   * она общая: одна и та же строка на двух экранах читается одинаково.
   */
  rowClass: string;
}

export interface NowNextHandle {
  /** Один кадр. `null` — плеер позиции не отдал (ещё не готов, нет источника). */
  sync(posSec: number | null): void;
  destroy(): void;
}

const el = (tag: string, cls: string, txt: string): HTMLElement => {
  const e = document.createElement(tag);
  e.className = cls;
  e.textContent = txt;
  return e;
};

/** Имя секции, или '' — секция безымянная (проигрыш, обрыв, хвост). */
const nameOf = (s: SectionSec | undefined): string => (s?.name ?? '').trim();

/**
 * Первая НАЗВАННАЯ секция после `i`, или черта.
 *
 * Именно она полезна во время проигрыша: «что дальше» — это следующее место,
 * которое читатель найдёт на листе, а безымянная секция блока не имеет вовсе
 * (chart-follow.ts). Ряд, называющий «дальше: ничего» посреди песни, назвал бы
 * дырой ровно тот момент, когда музыкант больше всего хочет знать, куда
 * смотреть.
 */
function nextNamed(sections: readonly SectionSec[], i: number): string {
  for (let j = i + 1; j < sections.length; j++) {
    const n = nameOf(sections[j]);
    if (n !== '') return n;
  }
  return DASH;
}

/** `null` = называть в этой песне нечего, и ряда быть не должно. */
export function mountNowNext(host: HTMLElement, deps: NowNextDeps): NowNextHandle | null {
  // Гейт по НАЗВАННЫМ секциям, а не по длине расписания: песня из одних
  // безымянных кусков не назовёт ни «сейчас», ни «дальше» ни в один момент, то
  // есть ряд стоял бы двумя чертами всё чтение — а это дыра в шапке, а не
  // сведения (шапка модуля).
  if (!deps.sections.some((s) => nameOf(s) !== '')) return null;

  const row = el('div', `cw-nn ${deps.rowClass}`, '');
  const cur = el('span', 'cw-nn-cur', DASH);
  const nxt = el('span', 'cw-nn-next', DASH);
  row.appendChild(cur);
  row.appendChild(nxt);
  host.appendChild(row);

  return {
    sync(posSec: number | null) {
      // Позиции нет — молчим обеими чертами, как и вне расписания: назвать
      // секцию, не зная, где песня, значит соврать.
      const i = posSec == null ? -1 : sectionIndexAt(deps.sections, posSec);
      // Внутри БЕЗЫМЯННОЙ секции «сейчас» — черта, а не пустое место: имени у
      // проигрыша нет, и молчание про него честно. А «дальше» при этом обязано
      // назвать следующую НАЗВАННУЮ секцию — это ровно то, чего в проигрыше
      // ждут.
      cur.textContent = i < 0 ? DASH : (nameOf(deps.sections[i]) || DASH);
      nxt.textContent = i < 0 ? DASH : nextNamed(deps.sections, i);
    },
    destroy() { row.remove(); },
  };
}
